import { Paper } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { makeStyles } from '@mui/styles'
import { styled } from '@mui/material/styles'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import logo3 from '../../../assets/image/dashboard3.png'
import AgroDashboardService from '../service/AgroDashboard'
import Controls from '../../../components/Entry/Controls'

const useStyles = makeStyles((theme) => ({
    pageContent: {
        margin: 10,
        backgroundColor: '#F2F8F6',
        '& .MuiPaper-root': {
            border: 'none',
            boxShadow: 'none'
        }
    }
}))

const TablePaper = styled(Paper)(({ theme }) => ({
    boxShadow: '0px 5px 5px -3px #d2d2d2,0px 8px 10px 1px #d2d2d2,0px 3px 14px 2px #d2d2d2'
}))

const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-white px-4 py-3 rounded-xl shadow-lg border border-emerald-100">
                <p className="text-xs text-gray-400 font-medium mb-1">Année {label}</p>
                <p className="text-sm text-gray-600">
                    Clients cette année :{' '}
                    <span className="font-semibold text-gray-800">{payload[0].payload.clients_count}</span>
                </p>
                <p className="text-sm text-emerald-600 mt-1">
                    Cumul total : <span className="font-bold">{payload[0].value}</span>
                </p>
            </div>
        )
    }
    return null
}

const DEFAULT_VALUES = {
    year: new Date().getFullYear(),
    customer_id: ''
}

export const CustomerDashBord = () => {
    const classes = useStyles()

    const [cumulativeData, setCumulativeData] = useState([])
    const [totalClients, setTotalClients] = useState(0)
    const [customers, setCustomers] = useState([])
    const [values, setValues] = useState({ ...DEFAULT_VALUES })

    // --- Pipeline des commandes ---
    const [pipeline, setPipeline] = useState({
        a_traiter: 0,
        en_cours: 0,
        livre: 0
    })
    const [kpis, setKpis] = useState({
        taux_conversion: 0,
        pourcentage_en_retard: 0
    })
    const [totalCommandes, setTotalCommandes] = useState(0)

    useEffect(() => {
        AgroDashboardService.getOrderCustomers()
            .then((data) => {
                if (data) {
                    setCustomers(data || [])
                }
            })
            .catch((error) => {
                console.error("Une erreur s'est produite lors de la récupération des clients :", error)
            })
    }, [])

    useEffect(() => {
        AgroDashboardService.getCustomersCumulative(values.year)
            .then((data) => {
                if (data) {
                    setCumulativeData(data || [])
                    const last = data[data.length - 1]
                    setTotalClients(last?.cumulative_clients || 0)
                }
            })
            .catch((error) => {
                console.error("Une erreur s'est produite lors de la récupération du cumul de clients :", error)
            })
    }, [values.year])

    useEffect(() => {
        AgroDashboardService.getOrderPipeline({
            plantation_type: 'Agroforesterie',
            year: values.year,
            customer_id: values.customer_id || undefined
        })
            .then((data) => {
                if (data) {
                    setPipeline(data.pipeline || { a_traiter: 0, en_cours: 0, livre: 0 })
                    setKpis(data.kpis || { taux_conversion: 0, pourcentage_en_retard: 0 })
                    setTotalCommandes(data.total_commandes || 0)
                }
            })
            .catch((error) => {
                console.error("Une erreur s'est produite lors de la récupération du pipeline des commandes :", error)
            })
    }, [values.year, values.customer_id])

    const getYears = () => {
        const currentYear = new Date().getFullYear()
        const options = []
        for (let i = currentYear - 4; i <= currentYear + 4; i++) {
            options.push(i)
        }
        return options
    }

    const handleChange = (e) => {
        const { name, value } = e.target
        setValues((prevValues) => ({
            ...prevValues,
            [name]: value
        }))
    }

    // ── handleReset ───────────────────────────────────────────────
    // Vide le filtre client ; seule l'année reste à sa valeur par
    // défaut (année courante).
    const handleReset = () => {
        setValues({ ...DEFAULT_VALUES })
    }

    return (
        <Paper className={classes.pageContent} elevation={0} style={{ backgroundColor: '#F2F8F6' }}>
            <TablePaper style={{ padding: 10, marginTop: 20, borderRadius: 10 }} elevation={8}>
                <div className="w-full">
                    <div className="flex justify-between">
                        <div>
                            <p className="font-semibold text-lg">Gestion des clients et des commandes clients</p>
                        </div>
                        <div>
                            <select
                                className="bg-[#0FCB8F] w-24 h-8 rounded-md border-none text-white"
                                name="year"
                                id="year"
                                value={values.year}
                                onChange={handleChange}
                            >
                                {getYears().map((year) => (
                                    <option key={year} value={year}>
                                        {year}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Filtres */}
                    <div className="flex items-end gap-[40px] mb-[15px] mt-[16px]">
                        <div className="w-[280px]">
                            <Controls.AutoCompleteSelect
                                label="Client"
                                options={customers}
                                onChange={handleChange}
                                name="customer_id"
                                value={values.customer_id || ''}
                                valueKey="id"
                                labelKey="firstname"
                            />
                        </div>
                        <button
                            type="button"
                            onClick={handleReset}
                            className="shrink-0 h-10 px-4 rounded-md border border-[#0FCB8F] text-[#0FCB8F] bg-white hover:bg-[#0FCB8F] hover:text-white transition-colors"
                        >
                            Réinitialiser
                        </button>
                    </div>

                    <div className="flex gap-10 mt-[16px]">
                        <div className="w-[20%] ">
                            <div className="flex border border-gray-200 bg-slate-200 rounded-xl">
                                <div>
                                    <div className="flex items-center gap-2 px-1 py-3 blend-darken">
                                        <img src={logo3} className="w-[50px]" />
                                    </div>
                                </div>
                                <div className="text-center p-[10px] m-[5px]">
                                    <p className="text-slate-500">Nombre Total </p>
                                    <p className="text-xl font-semibold text-green-500">{totalClients}</p>
                                </div>
                            </div>
                        </div>
                        <div className="w-full">
                            <div className="text-slate-500 ml-[10px] mb-2">Cumul annuel du nombre de clients</div>
                            <ResponsiveContainer width="100%" height={180}>
                                <AreaChart data={cumulativeData} margin={{ top: 5, right: 20, left: -10, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="colorClients" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.35} />
                                            <stop offset="95%" stopColor="#10b981" stopOpacity={0.02} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                                    <XAxis
                                        dataKey="year"
                                        stroke="#9ca3af"
                                        tick={{ fill: '#6b7280', fontSize: 12 }}
                                        axisLine={{ stroke: '#e5e7eb' }}
                                        tickLine={false}
                                    />
                                    <YAxis
                                        stroke="#9ca3af"
                                        tick={{ fill: '#6b7280', fontSize: 12 }}
                                        axisLine={false}
                                        tickLine={false}
                                        width={35}
                                    />
                                    <Tooltip
                                        content={<CustomTooltip />}
                                        cursor={{ stroke: '#10b981', strokeWidth: 1, strokeDasharray: '4 4' }}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="cumulative_clients"
                                        stroke="#059669"
                                        strokeWidth={3}
                                        fill="url(#colorClients)"
                                        dot={{ fill: '#059669', r: 4, strokeWidth: 2, stroke: '#fff' }}
                                        activeDot={{ r: 6 }}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* --- Pipeline des commandes --- */}
                    <div className="mt-8">
                        <div className="text-slate-500 ml-[10px] mb-2">Pipeline des commandes</div>

                        <div className="flex gap-10">
                            <div className="w-[20%]">
                                <div className="flex border border-gray-200 bg-slate-200 rounded-xl">
                                    <div>
                                        <div className="flex items-center gap-2 px-1 py-3 blend-darken">
                                            <img src={logo3} className="w-[50px]" />
                                        </div>
                                    </div>
                                    <div className="text-center p-[10px] m-[5px]">
                                        <p className="text-slate-500">Total commandes</p>
                                        <p className="text-xl font-semibold text-green-500">{totalCommandes}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="w-full flex gap-4">
                                <div className="flex-1 bg-orange-50 rounded-xl p-4 text-center">
                                    <p className="text-slate-500 text-sm">Commande à traiter</p>
                                    <p className="text-2xl font-bold text-orange-500 mt-1">{pipeline.a_traiter}</p>
                                </div>
                                <div className="flex-1 bg-blue-50 rounded-xl p-4 text-center">
                                    <p className="text-slate-500 text-sm">Commande validée</p>
                                    <p className="text-2xl font-bold text-blue-500 mt-1">{pipeline.en_cours}</p>
                                </div>
                                <div className="flex-1 bg-emerald-50 rounded-xl p-4 text-center">
                                    <p className="text-slate-500 text-sm">Plantation terminée</p>
                                    <p className="text-2xl font-bold text-emerald-600 mt-1">{pipeline.livre}</p>
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-10 mt-4">
                            <div className="w-[20%]" />
                            <div className="w-full flex gap-4">
                                <div className="flex-1 bg-slate-100 rounded-xl p-4">
                                    <p className="text-slate-500 text-sm">Taux de conversion</p>
                                    <p className="text-xl font-semibold text-green-600 mt-1">{kpis.taux_conversion}%</p>
                                </div>
                                <div className="flex-1 bg-slate-100 rounded-xl p-4">
                                    <p className="text-slate-500 text-sm">% en retard</p>
                                    <p className="text-xl font-semibold text-red-500 mt-1">
                                        {kpis.pourcentage_en_retard}%
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </TablePaper>
        </Paper>
    )
}
